import React from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Tag, Icon, Tooltip, Button, Checkbox } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import 'moment/locale/pt-br';

import { toBRL } from '~/components/UI/InputCurrency';
import * as bankReconciliationActions from '../actions';

// ─── Helpers ───────────────────────────────────────────────────────────────────

var TYPE_INFO = {
    mensalidade: { label: 'Mensalidade', color: 'blue' },
    extra: { label: 'Extra', color: 'purple' },
    comissao: { label: 'Comissão', color: 'orange' },
    despesa: { label: 'Despesa', color: 'red' },
    custom_credit: { label: 'Receita', color: 'green' },
    custom_debit: { label: 'Despesa', color: 'red' },
};

function getTypeInfo(type) {
    return TYPE_INFO[type] || { label: 'Outro', color: 'default' };
}

// Codigo humano que liga o recebimento a conta a receber: ME#### na mensalidade,
// #SOC### na cobranca vinda do societario. O backend ja manda pronto em `cod` nos
// payloads montados a mao (exact_match, sugestoes); nos modelos serializados o que
// chega e a coluna dash_task_id.
function getItemCod(item) {
    if (!item) return null;
    if (item.cod) return item.cod;
    if (item.dash_task_id) return '#SOC' + item.dash_task_id;
    return null;
}

function renderCod(cod) {
    if (!cod) return null;
    return React.createElement('span', { style: codBadgeStyle }, cod);
}

function getReconcilableType(type) {
    if (type === 'mensalidade') return 'App\\CustomerHonorary';
    if (type === 'extra') return 'App\\CustomerExtra';
    if (type === 'comissao') return 'App\\Refund';
    if (type === 'despesa') return 'App\\ContaPagar';
    return '';
}

// ─── Styles ────────────────────────────────────────────────────────────────────

var rowStyle = {
    display: 'flex', alignItems: 'stretch', gap: 0,
    padding: '12px 16px', borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.15s',
};

var codBadgeStyle = {
    fontSize: 11, fontWeight: 600, color: '#595959', background: '#f0f0f0',
    border: '1px solid #d9d9d9', borderRadius: 4, padding: '0 5px',
    whiteSpace: 'nowrap', flexShrink: 0, letterSpacing: '0.02em',
};

var cardBase = {
    flex: 1, background: '#fff', border: '1px solid #e8e8e8',
    borderRadius: 8, padding: '14px 16px', minWidth: 0,
};

var centerStyle = {
    width: 130, flexShrink: 0, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0 4px',
};

var tabStyle = function(isActive, color) {
    return {
        flex: 1, padding: '10px 0', textAlign: 'center', cursor: 'pointer',
        borderBottom: '2px solid ' + (isActive ? color : 'transparent'),
        color: isActive ? color : '#8c8c8c', fontWeight: isActive ? 600 : 400,
        fontSize: 14, transition: 'all 0.2s',
    };
};

// ─── Component ─────────────────────────────────────────────────────────────────

var CardList = React.memo(function CardList(props) {
    var filteredData = props.filteredData;
    var isLoading = props.isLoading;
    var rowSelection = props.rowSelection;
    var onReconcile = props.onReconcile;
    var onRemoveReconciliation = props.onRemoveReconciliation;
    var activeView = props.activeView;
    var onImport = props.onImport;

    var dispatch = useDispatch();
    var pendingMatches = useSelector(function(state) { return state.bankReconciliation.pendingMatches || {}; });

    // Local state
    var typeFilterState = React.useState('all');
    var typeFilter = typeFilterState[0];
    var setTypeFilter = typeFilterState[1];

    var sortState = React.useState('date_desc');
    var sortBy = sortState[0];
    var setSortBy = sortState[1];

    var reconcilingState = React.useState({});
    var reconcilingIds = reconcilingState[0];
    var setReconcilingIds = reconcilingState[1];

    var dismissedState = React.useState({});
    var dismissedMatches = dismissedState[0];
    var setDismissedMatches = dismissedState[1];

    // Selection
    var selectedKeys = rowSelection ? rowSelection.selectedRowKeys || [] : [];
    var selectedKeysSet = React.useMemo(function() { return new Set(selectedKeys); }, [selectedKeys]);

    function onToggleAll(e) {
        if (!rowSelection) return;
        if (e.target.checked) {
            rowSelection.onChange(displayData.map(function(r) { return r.id; }));
        } else {
            rowSelection.onChange([]);
        }
    }
    function onToggleRow(id) {
        if (!rowSelection) return;
        var idx = selectedKeys.indexOf(id);
        if (idx === -1) { rowSelection.onChange(selectedKeys.concat([id])); }
        else { rowSelection.onChange(selectedKeys.filter(function(k) { return k !== id; })); }
    }

    // Filtered + sorted data
    var counts = React.useMemo(function() {
        var all = 0, credits = 0, debits = 0;
        for (var i = 0; i < (filteredData || []).length; i++) {
            all++;
            if (filteredData[i].transaction_type === 'credit') credits++;
            else debits++;
        }
        return { all: all, credits: credits, debits: debits };
    }, [filteredData]);

    var displayData = React.useMemo(function() {
        var src = filteredData || [];
        // Type filter
        if (typeFilter === 'credit') {
            src = src.filter(function(r) { return r.transaction_type === 'credit'; });
        } else if (typeFilter === 'debit') {
            src = src.filter(function(r) { return r.transaction_type === 'debit'; });
        }
        // Sort
        var sorted = src.slice();
        if (sortBy === 'date_desc') sorted.sort(function(a, b) { return (b.transaction_date || '').localeCompare(a.transaction_date || ''); });
        else if (sortBy === 'date_asc') sorted.sort(function(a, b) { return (a.transaction_date || '').localeCompare(b.transaction_date || ''); });
        else if (sortBy === 'amount_desc') sorted.sort(function(a, b) { return (b.amount || 0) - (a.amount || 0); });
        else if (sortBy === 'amount_asc') sorted.sort(function(a, b) { return (a.amount || 0) - (b.amount || 0); });
        return sorted;
    }, [filteredData, typeFilter, sortBy]);

    var allChecked = displayData.length > 0 && selectedKeys.length === displayData.length;
    var someChecked = selectedKeys.length > 0 && !allChecked;

    // Inline reconcile for exact matches
    function handleInlineReconcile(record) {
        var match = record.exact_match;
        if (!match) return;
        var newReconciling = {};
        for (var k in reconcilingIds) { newReconciling[k] = true; }
        newReconciling[record.id] = true;
        setReconcilingIds(newReconciling);

        dispatch(bankReconciliationActions.createReconciliation({
            bank_statement_id: record.id,
            reconciliations: [{
                reconcilable_type: match.reconcilable_type || getReconcilableType(match.type),
                reconcilable_id: match.id,
            }],
        })).then(function() {
            var updated = {};
            for (var j in reconcilingIds) { if (j !== String(record.id)) updated[j] = true; }
            setReconcilingIds(updated);
            dispatch(bankReconciliationActions.getStats());
        }).catch(function() {
            var updated = {};
            for (var j in reconcilingIds) { if (j !== String(record.id)) updated[j] = true; }
            setReconcilingIds(updated);
        });
    }

    // ─── Render helpers ────────────────────────────────────────────────────────

    function renderLeftCard(record) {
        var isCredit = record.transaction_type === 'credit';
        var amountColor = isCredit ? '#52c41a' : '#f5222d';
        var prefix = isCredit ? '+' : '-';
        var bank = record.bank_account;
        var detected = record.detected_customer;
        var dateMoment = record.transaction_date ? moment(record.transaction_date) : null;

        return React.createElement('div', { style: Object.assign({}, cardBase, isCredit ? { borderLeft: '3px solid #52c41a' } : { borderLeft: '3px solid #f5222d' }) },
            // Row 1: Date + Bank
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 } },
                React.createElement('span', { style: { fontSize: 12, color: '#8c8c8c' } },
                    dateMoment ? dateMoment.format('DD/MM/YYYY') + '  ' + dateMoment.format('dddd') : '-'
                ),
                bank ? React.createElement(Tooltip, { title: bank.bank_code ? bank.name + ' (' + bank.bank_code + ')' : bank.name },
                    React.createElement(Tag, { style: { margin: 0, fontSize: 11 } }, bank.name)
                ) : null
            ),
            // Row 2: Amount
            React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: amountColor, marginBottom: 8 } },
                prefix + ' R$ ' + toBRL(record.amount || 0)
            ),
            // Row 3: Description
            React.createElement(Tooltip, { title: record.description, placement: 'topLeft' },
                React.createElement('div', { style: { fontSize: 13, color: '#595959', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: detected ? 8 : 0 } },
                    record.description || '-'
                )
            ),
            // Row 4: Doc number + detected customer hint
            (record.document_number || detected) ? React.createElement('div', { style: { fontSize: 11, color: '#bfbfbf', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 } },
                record.document_number ? React.createElement('span', null, 'Doc: ' + record.document_number) : null,
                detected ? React.createElement(Tooltip, { title: 'Cliente detectado via CPF/CNPJ: ' + detected.name },
                    React.createElement('span', { style: { color: '#13c2c2', display: 'inline-flex', alignItems: 'center', gap: 3 } },
                        React.createElement(Icon, { type: 'idcard', style: { fontSize: 10 } }),
                        'Cliente identificado'
                    )
                ) : null
            ) : null,
            // Ignorar button for pending items
            record.status === 'pending' ? React.createElement('div', { style: { marginTop: 10, borderTop: '1px solid #f0f0f0', paddingTop: 8, textAlign: 'right' } },
                React.createElement(Button, {
                    size: 'small', icon: 'eye-invisible', type: 'link',
                    onClick: function(e) {
                        e.stopPropagation();
                        dispatch(bankReconciliationActions.ignoreStatements([record.id])).then(function() {
                            dispatch(bankReconciliationActions.getStats());
                        });
                    },
                    style: { fontSize: 12, color: '#bfbfbf', padding: 0 }
                }, 'Ignorar')
            ) : null
        );
    }

    function renderCenter(record) {
        var isPending = record.status === 'pending';
        var isReconciled = record.status === 'reconciled';
        var isDismissed = !!dismissedMatches[record.id];
        var exactMatch = isDismissed ? null : record.exact_match;
        var alreadyMatch = record.already_reconciled_match;
        var isReconciling = !!reconcilingIds[record.id];

        if (isReconciled) {
            return React.createElement('div', { style: centerStyle },
                React.createElement('div', { style: { width: 1, height: 12, background: '#b7eb8f' } }),
                React.createElement('div', { style: { width: 32, height: 32, borderRadius: '50%', background: '#f6ffed', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                    React.createElement(Icon, { type: 'check', style: { color: '#52c41a', fontSize: 16 } })
                ),
                React.createElement('span', { style: { fontSize: 11, color: '#52c41a', fontWeight: 600 } }, 'Conciliado'),
                React.createElement('div', { style: { width: 1, height: 12, background: '#b7eb8f' } })
            );
        }

        if (isPending && exactMatch) {
            return React.createElement('div', { style: centerStyle },
                React.createElement('div', { style: { width: 1, height: 12, background: '#b7eb8f' } }),
                React.createElement('div', { style: { background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 12, padding: '3px 10px', fontSize: 11, color: '#389e0d', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' } },
                    React.createElement(Icon, { type: exactMatch.matched_by === 'codigo' ? 'barcode' : 'star', style: { fontSize: 10 } }),
                    // Casou pelo código ME/SOC do lançamento, não pelo valor
                    exactMatch.matched_by === 'codigo' ? 'Pelo código' : 'Encontramos'
                ),
                React.createElement(Button, {
                    type: 'primary', size: 'small', icon: isReconciling ? 'loading' : 'link',
                    loading: isReconciling,
                    onClick: function(e) { e.stopPropagation(); handleInlineReconcile(record); },
                    style: { borderRadius: 6, fontWeight: 500, width: '100%' }
                }, 'Conciliar'),
                React.createElement('div', { style: { width: 1, height: 12, background: '#b7eb8f' } })
            );
        }

        if (isPending && alreadyMatch) {
            return React.createElement('div', { style: centerStyle },
                React.createElement(Tag, { color: 'default', style: { margin: 0, fontSize: 11 } },
                    React.createElement(Icon, { type: 'check-circle', style: { marginRight: 4 } }),
                    'Já conciliado'
                )
            );
        }

        // Pending match selected from drawer — show enabled Conciliar
        var pendingMatch = pendingMatches[record.id];
        // Normalize pendingMatch → always an object with `items` array and optional `note`
        var pendingItems = pendingMatch ? (pendingMatch.items || [pendingMatch]) : [];
        var pendingNote = pendingMatch ? (pendingMatch.note || null) : null;
        if (isPending && pendingItems.length > 0) {
            var selectedLabel = pendingItems.length > 1 ? pendingItems.length + ' selecionados' : 'Selecionado';
            return React.createElement('div', { style: centerStyle },
                React.createElement('div', { style: { width: 1, height: 12, background: '#1890ff' } }),
                React.createElement('div', { style: { background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 12, padding: '3px 10px', fontSize: 11, color: '#1890ff', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' } },
                    React.createElement(Icon, { type: 'check-circle', style: { fontSize: 10 } }),
                    selectedLabel
                ),
                React.createElement(Button, {
                    type: 'primary', size: 'small', icon: isReconciling ? 'loading' : 'link',
                    loading: isReconciling,
                    onClick: function(e) {
                        e.stopPropagation();
                        var reconciliations = pendingItems.map(function(m) {
                            return { reconcilable_type: getReconcilableType(m.type), reconcilable_id: m.id };
                        });
                        var newRec = {};
                        for (var k in reconcilingIds) { newRec[k] = true; }
                        newRec[record.id] = true;
                        setReconcilingIds(newRec);
                        dispatch(bankReconciliationActions.createReconciliation({
                            bank_statement_id: record.id,
                            justification: pendingNote,
                            reconciliations: reconciliations,
                        })).then(function() {
                            dispatch(bankReconciliationActions.setPendingMatch(record.id, null));
                            dispatch(bankReconciliationActions.getStats());
                            var updated = {};
                            for (var j in reconcilingIds) { if (j !== String(record.id)) updated[j] = true; }
                            setReconcilingIds(updated);
                        }).catch(function() {
                            var updated = {};
                            for (var j in reconcilingIds) { if (j !== String(record.id)) updated[j] = true; }
                            setReconcilingIds(updated);
                        });
                    },
                    style: { borderRadius: 6, fontWeight: 500, width: '100%' }
                }, 'Conciliar'),
                React.createElement('div', { style: { width: 1, height: 12, background: '#1890ff' } })
            );
        }

        // No match — show disabled conciliar + connecting line
        if (isPending) {
            return React.createElement('div', { style: centerStyle },
                React.createElement('div', { style: { width: 1, height: 20, background: '#e8e8e8' } }),
                React.createElement(Tooltip, { title: 'Busque uma correspondência primeiro' },
                    React.createElement('div', null,
                        React.createElement(Button, {
                            size: 'small', icon: 'link', disabled: true,
                            style: { borderRadius: 6, fontWeight: 500 }
                        }, 'Conciliar')
                    )
                ),
                React.createElement('div', { style: { width: 1, height: 20, background: '#e8e8e8' } })
            );
        }

        return React.createElement('div', { style: centerStyle });
    }

    function renderRightCard(record) {
        var isPending = record.status === 'pending';
        var isReconciled = record.status === 'reconciled';
        var isDismissed = !!dismissedMatches[record.id];
        var exactMatch = isDismissed ? null : record.exact_match;
        var alreadyMatch = isDismissed ? null : record.already_reconciled_match;

        // Reconciled: show reconciliation details
        if (isReconciled) {
            var reconciliations = record.reconciliations || [];
            if (reconciliations.length === 0) {
                return React.createElement('div', { style: Object.assign({}, cardBase, { background: '#f6ffed', borderColor: '#b7eb8f' }) },
                    React.createElement('span', { style: { color: '#52c41a', fontSize: 13 } }, 'Conciliado')
                );
            }

            var isCreditStatement = record.transaction_type === 'credit';
            var typeKeyOf = function(recType) {
                if (recType.indexOf('CustomerHonorary') > -1) return 'mensalidade';
                if (recType.indexOf('CustomerExtra') > -1) return 'extra';
                if (recType.indexOf('Refund') > -1) return 'comissao';
                if (recType.indexOf('ContaPagar') > -1) return 'despesa';
                if (recType.indexOf('BankReconciliationEntry') > -1) return isCreditStatement ? 'custom_credit' : 'custom_debit';
                return 'outro';
            };
            var nameOf = function(rec) {
                var r = rec.reconcilable || {};
                var rt = rec.reconcilable_type || '';
                var isExpense = rt.indexOf('ContaPagar') > -1 || rt.indexOf('BankReconciliationEntry') > -1;
                return isExpense ? (r.description || '') : (r.customer ? r.customer.name : '');
            };
            var amountOf = function(rec) {
                var r = rec.reconcilable || {};
                var tk = typeKeyOf(rec.reconcilable_type || '');
                if (tk === 'mensalidade') {
                    return (parseFloat(r.amount) || 0) + (parseFloat(r.additionals_amount) || 0) + (parseFloat(r.amount13) || 0);
                }
                return parseFloat(r.amount) || parseFloat(r.total) || 0;
            };

            var totalReconciled = reconciliations.reduce(function(s, r) { return s + amountOf(r); }, 0);
            var isMulti = reconciliations.length > 1;
            var firstType = typeKeyOf(reconciliations[0].reconcilable_type || '');
            var headerTi = getTypeInfo(firstType);
            var multiLabel = isCreditStatement
                ? reconciliations.length + ' recebimento' + (reconciliations.length > 1 ? 's' : '')
                : reconciliations.length + ' pagamento' + (reconciliations.length > 1 ? 's' : '');
            var headerLabel = isMulti ? multiLabel : headerTi.label;

            // Color-code the card and accents by transaction direction
            var cardBg = isCreditStatement ? '#f6ffed' : '#fff7e6';
            var cardBorder = isCreditStatement ? '#b7eb8f' : '#ffd591';
            var accentColor = isCreditStatement ? '#389e0d' : '#d46b08';
            var dashBorder = isCreditStatement ? '#d9f7be' : '#ffe7ba';
            var directionIcon = isCreditStatement ? 'arrow-down' : 'arrow-up';
            var directionLabel = isCreditStatement ? 'Recebido' : 'Pago';

            return React.createElement('div', { style: Object.assign({}, cardBase, { background: cardBg, borderColor: cardBorder }) },
                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 6 } },
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 } },
                        React.createElement('span', {
                            style: {
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: 20, height: 20, borderRadius: '50%',
                                background: accentColor, color: '#fff', flexShrink: 0,
                            }
                        }, React.createElement(Icon, { type: directionIcon, style: { fontSize: 10 } })),
                        React.createElement(Tag, {
                            color: isCreditStatement ? 'green' : 'orange',
                            style: { margin: 0 }
                        }, headerLabel),
                        !isMulti && React.createElement('span', { style: { fontSize: 10, color: accentColor, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em' } }, directionLabel)
                    ),
                    React.createElement(Tooltip, { title: 'Remover conciliação' },
                        React.createElement(Button, {
                            size: 'small', icon: 'disconnect', type: 'link',
                            style: { color: '#f5222d', padding: 0 },
                            onClick: function(e) { e.stopPropagation(); onRemoveReconciliation(record); }
                        })
                    )
                ),
                isMulti ? (
                    React.createElement('div', null,
                        reconciliations.slice(0, 3).map(function(rec, idx) {
                            return React.createElement('div', { key: idx, style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: idx < Math.min(reconciliations.length, 3) - 1 ? '1px dashed ' + dashBorder : 'none', fontSize: 12 } },
                                React.createElement('span', { style: { display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0, marginRight: 8 } },
                                    renderCod(getItemCod(rec.reconcilable)),
                                    React.createElement('span', { style: { color: '#262626', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, nameOf(rec) || '-')
                                ),
                                React.createElement('span', { style: { color: accentColor, fontWeight: 600, flexShrink: 0 } }, (isCreditStatement ? '+ ' : '- ') + 'R$ ' + toBRL(amountOf(rec)))
                            );
                        }),
                        reconciliations.length > 3 && React.createElement('div', { style: { fontSize: 11, color: '#8c8c8c', marginTop: 4 } }, '+ ' + (reconciliations.length - 3) + ' mais'),
                        React.createElement('div', { style: { marginTop: 8, paddingTop: 8, borderTop: '1px solid ' + cardBorder, display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
                            React.createElement('span', { style: { fontSize: 12, color: '#595959' } }, 'Total ' + (isCreditStatement ? 'recebido' : 'pago')),
                            React.createElement('span', { style: { fontSize: 16, fontWeight: 700, color: accentColor } }, (isCreditStatement ? '+ ' : '- ') + 'R$ ' + toBRL(totalReconciled))
                        )
                    )
                ) : (function() {
                    var rec = reconciliations[0];
                    var reconcilable = rec.reconcilable || {};
                    var displayName = nameOf(rec);
                    var displayAmount = amountOf(rec);
                    var hasBreakdown = false;
                    var breakdown = [];
                    if (firstType === 'mensalidade') {
                        var mAdic = parseFloat(reconcilable.additionals_amount) || 0;
                        var m13 = parseFloat(reconcilable.amount13) || 0;
                        if (mAdic > 0 || m13 > 0) {
                            hasBreakdown = true;
                            breakdown.push('Mens: R$ ' + toBRL(parseFloat(reconcilable.amount) || 0));
                            if (mAdic > 0) breakdown.push('Adic: R$ ' + toBRL(mAdic));
                            if (m13 > 0) breakdown.push('13º: R$ ' + toBRL(m13));
                        }
                    }
                    return React.createElement(React.Fragment, null,
                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, minWidth: 0 } },
                            renderCod(getItemCod(reconcilable)),
                            React.createElement('span', { style: { fontSize: 14, fontWeight: 600, color: '#262626', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, displayName || '-')
                        ),
                        React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: accentColor } },
                            (isCreditStatement ? '+ ' : '- ') + 'R$ ' + toBRL(displayAmount),
                            hasBreakdown && React.createElement(Tooltip, { title: breakdown.join(' • ') },
                                React.createElement('span', { style: { marginLeft: 6, fontSize: 11, color: '#8c8c8c', fontWeight: 400, cursor: 'help' } },
                                    React.createElement(Icon, { type: 'info-circle', style: { marginRight: 3 } }),
                                    'Saldo'
                                )
                            )
                        )
                    );
                })()
            );
        }

        // Pending with exact match
        if (isPending && exactMatch) {
            var eti = getTypeInfo(exactMatch.type);
            return React.createElement('div', { style: Object.assign({}, cardBase, { background: '#fafafa', borderStyle: 'dashed', borderColor: '#b7eb8f' }) },
                React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 } },
                    (exactMatch.due_date || exactMatch.apuration_at) ? React.createElement('span', { style: { fontSize: 12, color: '#8c8c8c', display: 'flex', alignItems: 'center', gap: 4 } },
                        React.createElement(Icon, { type: 'calendar', style: { fontSize: 11 } }),
                        exactMatch.due_date ? 'Venc: ' + moment(exactMatch.due_date).format('DD/MM/YYYY') : 'Comp: ' + moment(exactMatch.apuration_at).format('MM/YYYY')
                    ) : React.createElement('span', null),
                    React.createElement('span', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                        React.createElement(Tag, { color: eti.color, style: { margin: 0 } }, eti.label),
                        React.createElement(Tooltip, { title: 'Descartar sugestão' },
                            React.createElement(Button, {
                                size: 'small', icon: 'disconnect', type: 'link',
                                style: { color: '#f5222d', padding: 0 },
                                onClick: function(e) {
                                    e.stopPropagation();
                                    var next = Object.assign({}, dismissedMatches);
                                    next[record.id] = true;
                                    setDismissedMatches(next);
                                }
                            })
                        )
                    )
                ),
                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, minWidth: 0 } },
                    renderCod(getItemCod(exactMatch)),
                    React.createElement('span', { style: { fontSize: 14, fontWeight: 600, color: '#262626', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, exactMatch.customer_name || '-')
                ),
                React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: '#389e0d' } }, 'R$ ' + toBRL(exactMatch.amount || 0))
            );
        }

        // Pending with already reconciled match
        if (isPending && alreadyMatch) {
            return React.createElement('div', { style: Object.assign({}, cardBase, { background: '#fafafa', borderStyle: 'dashed' }) },
                React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c', marginBottom: 4 } }, 'Correspondência já conciliada:'),
                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 } },
                    renderCod(getItemCod(alreadyMatch)),
                    React.createElement('span', { style: { fontSize: 13, fontWeight: 500, color: '#595959', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, alreadyMatch.customer_name || '-')
                ),
                React.createElement('div', { style: { fontSize: 13, color: '#8c8c8c' } }, 'R$ ' + toBRL(alreadyMatch.amount || 0))
            );
        }

        // Pending match selected from drawer
        var pendingMatch2 = pendingMatches[record.id];
        var pendingItems2 = pendingMatch2 ? (pendingMatch2.items || [pendingMatch2]) : [];
        if (isPending && pendingItems2.length > 0) {
            var totalAmount = pendingItems2.reduce(function(sum, m) { return sum + (parseFloat(m.amount) || 0); }, 0);
            var firstItem = pendingItems2[0];
            var pti = getTypeInfo(firstItem.type);
            var headerLabel = pendingItems2.length > 1 ? (pendingItems2.length + ' itens') : pti.label;
            var bodyTitle;
            if (pendingItems2.length > 1) {
                bodyTitle = React.createElement('div', null,
                    pendingItems2.slice(0, 2).map(function(m, i) {
                        var nm = m.type === 'despesa' ? (m.description || '') : (m.customer ? m.customer.name : '');
                        return React.createElement('div', { key: i, style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#595959', minWidth: 0 } },
                            renderCod(getItemCod(m)),
                            React.createElement('span', { style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, nm || '-')
                        );
                    }),
                    pendingItems2.length > 2 && React.createElement('div', { style: { fontSize: 11, color: '#8c8c8c', marginTop: 2 } }, '+' + (pendingItems2.length - 2) + ' mais')
                );
            } else {
                var pName = firstItem.type === 'despesa' ? (firstItem.description || '') : (firstItem.customer ? firstItem.customer.name : '');
                bodyTitle = React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, minWidth: 0 } },
                    renderCod(getItemCod(firstItem)),
                    React.createElement('span', { style: { fontSize: 14, fontWeight: 600, color: '#262626', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, pName || '-')
                );
            }
            return React.createElement('div', { style: Object.assign({}, cardBase, { background: '#e6f7ff', borderColor: '#91d5ff' }) },
                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 } },
                    React.createElement(Tag, { color: pti.color, style: { margin: 0 } }, headerLabel),
                    React.createElement(Tooltip, { title: 'Remover seleção' },
                        React.createElement(Button, {
                            size: 'small', icon: 'close', type: 'link',
                            style: { color: '#8c8c8c', padding: 0 },
                            onClick: function(e) { e.stopPropagation(); dispatch(bankReconciliationActions.setPendingMatch(record.id, null)); }
                        })
                    )
                ),
                bodyTitle,
                React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: '#1890ff', marginTop: pendingItems2.length > 1 ? 6 : 0 } }, 'R$ ' + toBRL(totalAmount))
            );
        }

        // No match — show "Buscar" as clickable placeholder
        return React.createElement('div', {
            style: Object.assign({}, cardBase, { background: '#fafafa', borderStyle: 'dashed', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }),
            onClick: function() { onReconcile(record); },
            onMouseEnter: function(e) { e.currentTarget.style.borderColor = '#722ed1'; e.currentTarget.style.background = '#f9f0ff'; },
            onMouseLeave: function(e) { e.currentTarget.style.borderColor = '#e8e8e8'; e.currentTarget.style.background = '#fafafa'; },
        },
            React.createElement(Icon, { type: 'search', style: { fontSize: 20, color: '#d9d9d9', marginBottom: 6 } }),
            React.createElement('span', { style: { fontSize: 13, color: '#bfbfbf' } }, 'Buscar correspondência')
        );
    }

    // ─── Item renderer ─────────────────────────────────────────────────────────

    function itemContent(index, record) {
        var isSelected = selectedKeysSet.has(record.id);
        var isReconciled = record.status === 'reconciled';
        var bg = isReconciled ? 'rgba(82,196,26,0.04)' : isSelected ? 'rgba(24,144,255,0.04)' : 'transparent';

        return React.createElement('div', { style: Object.assign({}, rowStyle, { background: bg }) },
            // Checkbox
            React.createElement('div', { style: { display: 'flex', alignItems: 'flex-start', paddingTop: 16, paddingRight: 8 } },
                React.createElement(Checkbox, { checked: isSelected, onChange: function() { onToggleRow(record.id); } })
            ),
            // Left card
            renderLeftCard(record),
            // Center indicator
            renderCenter(record),
            // Right card
            renderRightCard(record)
        );
    }

    // ─── Empty state ───────────────────────────────────────────────────────────

    // Skeleton loading
    if (isLoading) {
        var skeletonBar = function(w, h, mb) {
            return React.createElement('div', { style: { width: w, height: h || 14, borderRadius: 4, background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', marginBottom: mb || 0 } });
        };
        var skeletonCard = function(key) {
            return React.createElement('div', { key: key, style: Object.assign({}, rowStyle, { background: '#fff' }) },
                React.createElement('div', { style: { paddingTop: 16, paddingRight: 8 } },
                    React.createElement('div', { style: { width: 16, height: 16, borderRadius: 3, background: '#f0f0f0' } })
                ),
                React.createElement('div', { style: Object.assign({}, cardBase) },
                    skeletonBar('40%', 12, 10),
                    skeletonBar('60%', 22, 10),
                    skeletonBar('80%', 14, 0)
                ),
                React.createElement('div', { style: centerStyle },
                    React.createElement('div', { style: { width: 1, height: 40, background: '#f0f0f0' } })
                ),
                React.createElement('div', { style: Object.assign({}, cardBase, { borderStyle: 'dashed' }) },
                    skeletonBar('50%', 12, 10),
                    skeletonBar('40%', 18, 10),
                    skeletonBar('30%', 14, 0)
                )
            );
        };
        return React.createElement('div', null,
            React.createElement('style', { dangerouslySetInnerHTML: { __html: '@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }' } }),
            React.createElement('div', { style: { border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' } },
                skeletonCard('sk-1'), skeletonCard('sk-2'), skeletonCard('sk-3'), skeletonCard('sk-4'), skeletonCard('sk-5')
            )
        );
    }

    if (!displayData || displayData.length === 0) {
        var emptyIcon = activeView === 'pending' ? 'check-circle' : activeView === 'reconciled' ? 'calendar' : 'inbox';
        var emptyColor = activeView === 'pending' ? '#52c41a' : activeView === 'reconciled' ? '#1890ff' : '#bfbfbf';
        var emptyTitle = activeView === 'pending' ? 'Nenhum lançamento pendente!' : activeView === 'reconciled' ? 'Nenhum lançamento conciliado neste período' : 'Nenhum lançamento encontrado';
        var emptyDesc = activeView === 'pending' ? 'Todos os lançamentos foram conciliados.' : 'Tente selecionar um período diferente.';

        return React.createElement('div', { style: { border: '1px solid #e8e8e8', borderRadius: 8, background: '#fff', padding: '60px 20px', textAlign: 'center' } },
            React.createElement(Icon, { type: emptyIcon, style: { fontSize: 48, color: emptyColor, marginBottom: 16 } }),
            React.createElement('h3', { style: { marginBottom: 8, color: 'rgba(0,0,0,0.85)', fontWeight: 500 } }, emptyTitle),
            React.createElement('p', { style: { color: 'rgba(0,0,0,0.45)' } }, emptyDesc),
            activeView === 'pending' && onImport ? React.createElement(Button, { type: 'primary', icon: 'upload', onClick: onImport }, 'Importar Extrato') : null
        );
    }

    // ─── Main render ───────────────────────────────────────────────────────────

    return React.createElement('div', null,
        // Summary tabs + sort
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e8e8e8', borderRadius: '8px 8px 0 0', borderBottom: 'none' } },
            // Checkbox all
            React.createElement('div', { style: { padding: '0 12px', borderRight: '1px solid #f0f0f0' } },
                React.createElement(Checkbox, { checked: allChecked, indeterminate: someChecked, onChange: onToggleAll })
            ),
            // Type tabs
            React.createElement('div', { style: { display: 'flex', flex: 1 } },
                React.createElement('div', { style: tabStyle(typeFilter === 'all', '#1890ff'), onClick: function() { setTypeFilter('all'); } },
                    React.createElement(Icon, { type: 'unordered-list', style: { marginRight: 6, fontSize: 13 } }),
                    'Todos ', React.createElement('strong', null, counts.all)
                ),
                React.createElement('div', { style: tabStyle(typeFilter === 'credit', '#52c41a'), onClick: function() { setTypeFilter('credit'); } },
                    React.createElement(Icon, { type: 'arrow-down', style: { marginRight: 6, fontSize: 13 } }),
                    'Recebimentos ', React.createElement('strong', null, counts.credits)
                ),
                React.createElement('div', { style: tabStyle(typeFilter === 'debit', '#f5222d'), onClick: function() { setTypeFilter('debit'); } },
                    React.createElement(Icon, { type: 'arrow-up', style: { marginRight: 6, fontSize: 13 } }),
                    'Pagamentos ', React.createElement('strong', null, counts.debits)
                )
            ),
            // Sort
            React.createElement('div', { style: { padding: '0 12px', borderLeft: '1px solid #f0f0f0' } },
                React.createElement(Tooltip, { title: 'Ordenar' },
                    React.createElement(Button, {
                        size: 'small', icon: 'sort-ascending', type: 'link',
                        style: { color: '#8c8c8c' },
                        onClick: function() {
                            var next = { date_desc: 'date_asc', date_asc: 'amount_desc', amount_desc: 'amount_asc', amount_asc: 'date_desc' };
                            setSortBy(next[sortBy] || 'date_desc');
                        }
                    },
                        sortBy.indexOf('date') > -1 ? 'Data' : 'Valor',
                        React.createElement(Icon, { type: sortBy.indexOf('asc') > -1 ? 'caret-up' : 'caret-down', style: { marginLeft: 4 } })
                    )
                )
            )
        ),

        // Column headers
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', padding: '10px 16px', background: '#fff', borderLeft: '1px solid #e8e8e8', borderRight: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' } },
            React.createElement('div', { style: { width: 30 } }),
            React.createElement('div', { style: { flex: 1, display: 'flex', alignItems: 'center', gap: 8 } },
                React.createElement('div', { style: { width: 24, height: 24, borderRadius: 6, background: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                    React.createElement(Icon, { type: 'bank', style: { fontSize: 12, color: '#1890ff' } })
                ),
                React.createElement('span', { style: { color: '#1890ff' } }, 'Lançamentos do Banco')
            ),
            React.createElement('div', { style: { width: 130, textAlign: 'center' } },
                React.createElement(Icon, { type: 'swap', style: { fontSize: 14, color: '#d9d9d9' } })
            ),
            React.createElement('div', { style: { flex: 1, display: 'flex', alignItems: 'center', gap: 8 } },
                React.createElement('div', { style: { width: 24, height: 24, borderRadius: 6, background: '#f9f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                    React.createElement(Icon, { type: 'reconciliation', style: { fontSize: 12, color: '#722ed1' } })
                ),
                React.createElement('span', { style: { color: '#722ed1' } }, 'Lançamentos do Contlabs')
            )
        ),

        // Card list
        React.createElement(Virtuoso, {
            style: { height: window.innerHeight - 360, borderLeft: '1px solid #e8e8e8', borderRight: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8', background: '#fafafa' },
            data: displayData,
            itemContent: itemContent,
            overscan: 15,
        }),

        // Footer
        React.createElement('div', { style: { padding: '8px 16px', background: '#fafafa', borderLeft: '1px solid #e8e8e8', borderRight: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8', borderRadius: '0 0 8px 8px', fontSize: 13, color: '#8c8c8c' } },
            'Total: ' + displayData.length + ' lançamento(s)'
        )
    );
});

export default CardList;
